import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Button, Modal, Select, Space, Spin, notification } from 'antd'
import { PlusOutlined } from '@ant-design/icons'

import ApplyFieldRuleTree from './ApplyFieldRuleTree'
import { buildGroupedFieldSelectOptions } from './applyFieldRuleOptions'
import {
  buildSaveApplyFieldRulePayload,
  parseApplyFieldRuleListToTree,
  validateApplyFieldRuleTree
} from './applyFieldRuleTransform'
import { findApplyFieldRule, saveApplyFieldRule } from '../../../../common/request'
import ResponseCode from '../../../../common/response-code'

const ConfigApplyFieldRuleModal = forwardRef((props, ref) => {
  const ruleTreeRef = useRef()
  const [notifyApi, contextHolder] = notification.useNotification()
  const [modalVisible, setModalVisible] = useState(false)
  const [availableFieldList, setAvailableFieldList] = useState([])
  const [fieldToAdd, setFieldToAdd] = useState(undefined)
  const [disabled, setDisabled] = useState(false)
  const [loadingFields, setLoadingFields] = useState(false)
  const [loadingRules, setLoadingRules] = useState(false)
  const [saving, setSaving] = useState(false)
  const [ruleListVersion, setRuleListVersion] = useState(0)

  const loadAvailableFieldList = () => {
    setLoadingFields(true)
    const fields = (props.applyFieldList || []).map(field => {
      if (String(field.fieldType) === '11') {
        return { ...field }
      }

      return {
        ...field,
        fieldFromType: field.fieldFromType || '2'
      }
    })

    setAvailableFieldList(fields)
    setLoadingFields(false)
  }

  const loadApplyFieldRules = async () => {
    if (!props.examInfo?.id) {
      ruleTreeRef.current?.resetRules()
      return
    }

    setLoadingRules(true)
    ruleTreeRef.current?.resetRules()

    try {
      const res = await findApplyFieldRule({ examId: String(props.examInfo.id) })
      if (res.code === ResponseCode.success) {
        const rules = parseApplyFieldRuleListToTree(res.data?.applyFieldRuleList)
        ruleTreeRef.current?.setRuleTreeData(rules)
        setRuleListVersion(version => version + 1)
      } else {
        Modal.error({
          title: '错误',
          content: res.msg,
          centered: true
        })
      }
    } catch {
      Modal.error({
        title: '网络错误',
        content: '查询报名字段规则失败',
        centered: true
      })
    } finally {
      setLoadingRules(false)
    }
  }

  useEffect(() => {
    if (modalVisible) {
      setDisabled(props.examInfo?.examinationStatus > 0)
      loadAvailableFieldList()
      loadApplyFieldRules()
    }
  }, [modalVisible])

  useImperativeHandle(ref, () => {
    return {
      showModal
    }
  })

  const showModal = () => {
    setModalVisible(true)
  }

  const hideModal = () => {
    setModalVisible(false)
    setFieldToAdd(undefined)
    ruleTreeRef.current?.resetRules()
  }

  const getFieldOptions = () => {
    void ruleListVersion
    const usedCodes = (ruleTreeRef.current?.getRuleTree()?.children || []).map(item => {
      return item.formData?.fieldCode
    })

    return buildGroupedFieldSelectOptions(availableFieldList, {
      excludeFieldCodes: usedCodes
    })
  }

  const handleAddRuleField = () => {
    const field = availableFieldList.find(item => item.fieldCode === fieldToAdd)
    if (!field) {
      Modal.warning({
        title: '提示',
        content: '请选择要添加的字段',
        centered: true
      })
      return
    }

    const added = ruleTreeRef.current?.addRuleByField(field)
    if (added) {
      setFieldToAdd(undefined)
      setRuleListVersion(version => version + 1)
    } else {
      Modal.warning({
        title: '提示',
        content: '该字段已在规则列表中',
        centered: true
      })
    }
  }

  const getRuleData = () => {
    const ruleTree = ruleTreeRef.current?.getRuleTree()
    return buildSaveApplyFieldRulePayload({
      examId: props.examInfo?.id,
      applyFieldList: props.applyFieldList,
      ruleTreeChildren: ruleTree?.children
    })
  }

  const onOk = async () => {
    const ruleTree = ruleTreeRef.current?.getRuleTree()
    const validationErrors = validateApplyFieldRuleTree(ruleTree?.children)

    if (validationErrors.length) {
      Modal.warning({
        title: '请完善规则配置',
        content: validationErrors.join('\n'),
        centered: true
      })
      return
    }

    const data = getRuleData()

    setSaving(true)
    try {
      const res = await saveApplyFieldRule(data)
      if (res.code === ResponseCode.success) {
        notifyApi.success({
          message: '规则配置已保存'
        })
        props.onSave?.(data)
        hideModal()
      } else {
        Modal.error({
          title: '错误',
          content: res.msg,
          centered: true
        })
      }
    } catch {
      Modal.error({
        title: '网络错误',
        content: '保存报名字段规则失败',
        centered: true
      })
    } finally {
      setSaving(false)
    }
  }

  const getModalFooter = () => {
    if (disabled) {
      return [
        <Button key="close" onClick={ hideModal }>关闭</Button>
      ]
    }
    return undefined
  }

  return (
    <div ref={ ref }>
      { contextHolder }
      <Modal
        title={ disabled ? '字段规则配置（只读）' : '字段规则配置' }
        open={ modalVisible }
        width={ 1100 }
        onOk={ onOk }
        onCancel={ hideModal }
        confirmLoading={ saving }
        footer={ getModalFooter() }
        destroyOnClose
      >
        <Spin spinning={ loadingFields || loadingRules }>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div>
              {
                disabled
                  ? '报名已开始，当前仅可查看规则配置，不可修改'
                  : `共 ${ availableFieldList.length } 个已配置字段可选，可添加为规则并配置子规则`
              }
            </div>
          </div>

          {
            !disabled && !loadingFields && !loadingRules && (
              <Space className="mb-3">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="选择要添加的字段"
                  style={ { width: 360 } }
                  value={ fieldToAdd }
                  options={ getFieldOptions() }
                  onChange={ setFieldToAdd }
                ></Select>
                <Button
                  type="primary"
                  icon={ <PlusOutlined/> }
                  onClick={ handleAddRuleField }
                  disabled={ availableFieldList.length === 0 }
                >添加规则</Button>
              </Space>
            )
          }

          <div className="mt-2">
            <ApplyFieldRuleTree
              ref={ ruleTreeRef }
              selectedFieldList={ availableFieldList }
              disabled={ disabled }
            ></ApplyFieldRuleTree>
          </div>
        </Spin>
      </Modal>
    </div>
  )
})

export default ConfigApplyFieldRuleModal
